import { useEffect, useLayoutEffect, useReducer } from 'react'
import {
	Conversation,
	Message,
	ParticipantUpdateReason,
} from '@twilio/conversations'

import {
	CHAT_CHANNEL_REDUCER_ACTIONS,
	useChannelHelperReducer,
	useChannelHelperReducerInitialState,
} from './useChannelHelperReducer'
import { formatMessage } from '../useChatHelper'
import { ChatEvents } from '@app/types/chat'
import { ChatChannelBaseData } from './useChannelHelperTypes'
import { useDefaultComponentTools } from '../../../useDefaultComponentTools'
import { getLastSeenIndexes, getChatChannelData } from './useChannelHelpers'
import { useChatChannelSid } from '@app/pages/Dashboard/Routes/ChatPage/useChatChannelSid'

export interface ChatContextProps extends ChatChannelBaseData {
	readonly channelDataIsLoading: boolean
	readonly resetChannel: () => void
}

// TODO: need pagination

const useChannelHelper = (
	userIdentity: string,
	channel?: Conversation
): ChatContextProps => {
	const [
		{
			channelMessages,
			lastSeenIndex,
			lastSeenPatientIndex,
			isPatientOnline,
			userModelsForParticipants,
			channelDataIsLoading,
		},
		dispatch,
	] = useReducer(useChannelHelperReducer, useChannelHelperReducerInitialState)
	const { isMounted, alert } = useDefaultComponentTools()
	const openedChannelSid = useChatChannelSid()

	const resetChannel = (): void => {
		dispatch({
			type: CHAT_CHANNEL_REDUCER_ACTIONS.RESET,
		})
	}

	useLayoutEffect(() => {
		if (!channel?.sid) return

		dispatch({
			type: CHAT_CHANNEL_REDUCER_ACTIONS.SET_CHANNEL_DATA_LOADING_STATUS,
			payload: true,
		})

		getChatChannelData(channel, userIdentity)
			.then(chatData => {
				isMounted() &&
					chatData &&
					dispatch({
						type: CHAT_CHANNEL_REDUCER_ACTIONS.INIT,
						payload: {
							channelMessages: chatData.channelMessages,
							lastSeenIndex: chatData.lastSeenIndex,
							lastSeenPatientIndex: chatData.lastSeenPatientIndex,
							userModelsForParticipants:
								chatData.userModelsForParticipants,
							isPatientOnline: chatData.isPatientOnline,
							channelDataIsLoading: false,
						},
					})
			})
			.catch(e => {
				alert.error(
					'message' in e ? e.message : 'Get channel data error'
				)

				isMounted() &&
					dispatch({
						type: CHAT_CHANNEL_REDUCER_ACTIONS.SET_CHANNEL_DATA_LOADING_STATUS,
						payload: false,
					})
			})
	}, [channel?.sid, userIdentity])

	useEffect(() => {
		if (!channel?.sid) return

		const updateMessageStatus = async ({
			updateReasons,
		}: {
			updateReasons: ParticipantUpdateReason[]
		}): Promise<void> => {
			if (updateReasons.includes('lastReadMessageIndex')) {
				try {
					const participants = await channel?.getParticipants()

					const [lastSeenIndex, lastSeenPatientIndex] =
						getLastSeenIndexes(participants || [], userIdentity)

					isMounted() &&
						dispatch({
							type: CHAT_CHANNEL_REDUCER_ACTIONS.SET_LAST_SEEN_INDEX,
							payload: [lastSeenIndex, lastSeenPatientIndex],
						})
				} catch (e: any) {
					alert.error(
						'message' in e ? e.message : 'Get channel data error'
					)
				}
			}
		}

		const handleNewMessage = (msg: Message): void => {
			isMounted() &&
				dispatch({
					type: CHAT_CHANNEL_REDUCER_ACTIONS.SET_NEW_MESSAGE,
					payload: formatMessage(msg),
				})
		}

		channel?.on(ChatEvents.MESSAGE_ADDED, handleNewMessage)

		channel?.on(ChatEvents.PARTICIPANT_UPDATED, updateMessageStatus)

		return () => {
			channel?.off(ChatEvents.PARTICIPANT_UPDATED, updateMessageStatus)

			channel?.off(ChatEvents.MESSAGE_ADDED, handleNewMessage)
		}
	}, [channel?.sid, userIdentity, openedChannelSid])

	return {
		lastSeenIndex,
		lastSeenPatientIndex,
		channelMessages,
		isPatientOnline,
		userModelsForParticipants,
		channelDataIsLoading,
		resetChannel,
	}
}

export default useChannelHelper
